import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Store, Lock, ShoppingCart, Loader2, Shield, Copy, Check, LogOut, ChevronRight, X, History, TrendingUp, Wallet, Package, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CreditStorePublicPage() {
  const { ownerId } = useParams();
  const [storeInfo, setStoreInfo] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem('cs_token') || '');
  const [me, setMe] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [need2fa, setNeed2fa] = useState(false);
  const [modoCadastro, setModoCadastro] = useState(false);
  const [signupNome, setSignupNome] = useState('');
  const [vincModal, setVincModal] = useState(null);
  const [vincUser, setVincUser] = useState('');
  const [vincBusy, setVincBusy] = useState(false);
  const [totp, setTotp] = useState('');
  const [logging, setLogging] = useState(false);
  const [selInteg, setSelInteg] = useState(null);
  const [qtd, setQtd] = useState('');
  const [cotacao, setCotacao] = useState(null);
  const [comprando, setComprando] = useState(false);
  const [pix, setPix] = useState(null);
  const [view, setView] = useState('comprar');
  const [hist, setHist] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => { axios.get(`${API}/public/loja/${ownerId}/info`).then(r => setStoreInfo(r.data)).catch(() => setStoreInfo({ nome_loja: 'Loja' })); }, [ownerId]);
  const loadMe = async (tk) => { try { const r = await axios.get(`${API}/public/loja/me`, { headers: { Authorization: `Bearer ${tk}` } }); setMe(r.data); } catch { setToken(''); sessionStorage.removeItem('cs_token'); } };
  useEffect(() => { if (token) loadMe(token); }, [token]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  const loadHist = () => { axios.get(`${API}/public/loja/historico`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setHist(r.data)).catch(() => {}); };
  useEffect(() => { if (token && me && view === 'historico') loadHist(); }, [view, token, me]);
  useEffect(() => { if (pix && pix.aprovado) loadHist(); }, [pix]);

  const login = async () => {
    setLogging(true);
    try {
      const r = await axios.post(`${API}/public/loja/login`, { owner_id: ownerId, username: username.trim(), password, totp_code: totp || undefined });
      if (r.data.need_2fa) { setNeed2fa(true); setLogging(false); return; }
      setToken(r.data.token); sessionStorage.setItem('cs_token', r.data.token);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro no login'); }
    finally { setLogging(false); }
  };
  const signup = async () => {
    if (!username.trim() || !password.trim()) return toast.error('Preencha usuário e senha');
    setLogging(true);
    try {
      const r = await axios.post(`${API}/public/loja/signup`, { owner_id: ownerId, username: username.trim(), password, nome: signupNome || username.trim() });
      setToken(r.data.token); sessionStorage.setItem('cs_token', r.data.token);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro no cadastro'); }
    finally { setLogging(false); }
  };
  const vincular = async () => {
    if (!vincUser.trim()) return toast.error('Digite seu usuário no painel');
    setVincBusy(true);
    try {
      await axios.post(`${API}/public/loja/vincular-integracao`, { integration_id: vincModal.id, panel_username: vincUser.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Vinculado com sucesso!');
      setVincModal(null); setVincUser(''); loadMe(token);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao vincular'); }
    finally { setVincBusy(false); }
  };
  const desvincular = async (ig) => {
    if (!window.confirm(`Desvincular ${ig.nome}?`)) return;
    try { await axios.post(`${API}/public/loja/desvincular-integracao`, { integration_id: ig.id }, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Desvinculado'); loadMe(token); }
    catch { toast.error('Erro ao desvincular'); }
  };
  const cotar = async () => {
    if (!selInteg || !qtd) { setCotacao(null); return; }
    try { const r = await axios.post(`${API}/public/loja/cotar`, { integration_id: selInteg.id, quantidade: parseInt(qtd) }, { headers: { Authorization: `Bearer ${token}` } }); setCotacao(r.data); } catch { setCotacao(null); }
  };
  useEffect(() => { const t = setTimeout(cotar, 350); return () => clearTimeout(t); }, [qtd, selInteg]);
  const startPolling = (txId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try { const s = await axios.get(`${API}/public/loja/compra/${txId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (s.data.status === 'approved') { clearInterval(pollRef.current); pollRef.current = null; setPix(p => p ? { ...p, aprovado: true } : p); } } catch {}
    }, 5000);
  };
  const comprar = async (metodo = 'pix') => {
    if (!cotacao) return;
    setComprando(true);
    try {
      const r = await axios.post(`${API}/public/loja/comprar`, { integration_id: selInteg.id, quantidade: parseInt(qtd), metodo }, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      setPix(r.data); startPolling(r.data.transaction_id);
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao comprar'); }
    finally { setComprando(false); }
  };
  const fecharCompra = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } setPix(null); setQtd(''); setCotacao(null); };
  const fecharPainel = () => { fecharCompra(); setSelInteg(null); };
  const logout = () => { if (pollRef.current) clearInterval(pollRef.current); setToken(''); setMe(null); sessionStorage.removeItem('cs_token'); };
  const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  const styles = (
    <style>{`
      @keyframes csUp { from { opacity:0; transform: translateY(14px); } to { opacity:1; transform:none; } }
      @keyframes csSlide { from { transform: translateX(100%); } to { transform:none; } }
      @keyframes csFade { from { opacity:0; } to { opacity:1; } }
      @keyframes csPop { 0% { opacity:0; transform: scale(.94); } 100% { opacity:1; transform:none; } }
      @keyframes csFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }
      @keyframes csGlow { 0%,100% { opacity:.5; } 50% { opacity:.9; } }
      .csUp { animation: csUp .5s cubic-bezier(.16,.84,.44,1) both; }
      .csSlide { animation: csSlide .4s cubic-bezier(.16,.84,.44,1) both; }
      .csFade { animation: csFade .3s ease both; }
      .csPop { animation: csPop .4s cubic-bezier(.16,.84,.44,1) both; }
      .cs-bg { background:
        radial-gradient(1200px 600px at 50% -10%, rgba(99,102,241,.16), transparent 55%),
        radial-gradient(900px 500px at 100% 100%, rgba(14,165,233,.14), transparent 55%),
        radial-gradient(800px 500px at 0% 90%, rgba(139,92,246,.10), transparent 55%),
        #080b12; }
      .cs-orb { position:absolute; border-radius:9999px; filter: blur(60px); animation: csFloat 8s ease-in-out infinite; }
      .cs-glass { background: linear-gradient(155deg, rgba(30,41,66,.55), rgba(15,20,34,.55)); border:1px solid rgba(255,255,255,.09); backdrop-filter: blur(24px) saturate(1.2); box-shadow: 0 24px 60px -24px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.06); }
      .cs-field { background: rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.1); transition: all .15s; }
      .cs-field:focus { border-color: rgba(99,102,241,.6); box-shadow: 0 0 0 3px rgba(99,102,241,.15); outline:none; }
      .cs-btn { background: linear-gradient(135deg, #6366f1, #4f46e5); box-shadow: 0 10px 30px -8px rgba(99,102,241,.5); transition: all .15s; }
      .cs-btn:hover { filter: brightness(1.08); box-shadow: 0 14px 40px -8px rgba(99,102,241,.6); }
      .cs-btn:active { transform: scale(.98); }
    `}</style>
  );

  // ---------- LOGIN ----------
  if (!token || !me) {
    return (
      <div className="min-h-screen cs-bg relative overflow-hidden flex items-center justify-center p-4">
        {styles}
        <div className="cs-orb w-96 h-96 bg-indigo-600/20 -top-20 -left-20" />
        <div className="cs-orb w-[28rem] h-[28rem] bg-sky-500/15 -bottom-32 -right-24" style={{ animationDelay: '2s' }} />
        <div className="w-full max-w-sm relative csUp">
          <div className="cs-glass rounded-3xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mx-auto mb-4 shadow-xl shadow-indigo-500/30"><Store className="w-8 h-8 text-white" /></div>
              <h1 className="text-xl font-bold text-white tracking-tight">{storeInfo?.nome_loja || 'Loja de Créditos'}</h1>
              <p className="text-sm text-white/40 mt-1">{modoCadastro ? 'Criar sua conta' : 'Área do revendedor'}</p>
            </div>
            <div className="space-y-4">
              {!need2fa ? (
                <>
                  {modoCadastro && (
                    <div>
                      <label className="text-xs font-medium text-white/50 mb-1.5 block">Seu nome</label>
                      <input value={signupNome} onChange={e => setSignupNome(e.target.value)} className="cs-field w-full px-4 py-3 rounded-xl text-white text-sm" />
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-white/50 mb-1.5 block">Usuário</label>
                    <input value={username} onChange={e => setUsername(e.target.value)} className="cs-field w-full px-4 py-3 rounded-xl text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-white/50 mb-1.5 block">Senha</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && (modoCadastro ? signup() : login())} className="cs-field w-full px-4 py-3 rounded-xl text-white text-sm" />
                  </div>
                </>
              ) : (
                <div className="csPop">
                  <label className="text-xs font-medium text-white/50 mb-2 flex items-center gap-1.5 justify-center"><Shield className="w-3.5 h-3.5 text-indigo-400" /> Código de verificação</label>
                  <input value={totp} onChange={e => setTotp(e.target.value.replace(/\D/g, ''))} maxLength={6} autoFocus onKeyDown={e => e.key === 'Enter' && login()} className="cs-field w-full px-4 py-3 rounded-xl text-white text-center text-2xl tracking-[0.5em] font-semibold" />
                </div>
              )}
              <button onClick={modoCadastro ? signup : login} disabled={logging} className="cs-btn w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                {logging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} {modoCadastro ? 'Criar conta' : 'Entrar'}
              </button>
              {storeInfo?.signup_enabled && !need2fa && (
                <button onClick={() => setModoCadastro(!modoCadastro)} className="w-full text-center text-xs text-white/50 hover:text-white/80 mt-1">
                  {modoCadastro ? 'Já tenho conta — entrar' : 'Não tem conta? Criar agora'}
                </button>
              )}
            </div>
          </div>
          <p className="text-center text-white/20 text-xs mt-5 tracking-wide">POWERED BY NEXUZAP</p>
        </div>
      </div>
    );
  }

  // ---------- LOJA ----------
  const IntegIcon = ({ nome }) => (
    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500/25 to-sky-500/25 border border-white/10 flex items-center justify-center shrink-0 text-white font-bold text-lg">{(nome || '?')[0].toUpperCase()}</div>
  );

  return (
    <div className="min-h-screen cs-bg relative overflow-x-hidden">
      {styles}
      <div className="cs-orb w-[30rem] h-[30rem] bg-indigo-600/10 -top-40 left-1/4 pointer-events-none" />

      <header className="relative border-b border-white/[0.06] backdrop-blur-xl bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-lg shadow-indigo-500/25"><Store className="w-5 h-5 text-white" /></div>
            <div><p className="text-sm font-bold text-white leading-tight tracking-tight">{storeInfo?.nome_loja}</p><p className="text-[11px] text-white/40">Olá, {me.nome}</p></div>
          </div>
          <button onClick={logout} className="text-xs text-white/40 hover:text-white flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/[0.05] transition-colors"><LogOut className="w-3.5 h-3.5" /> Sair</button>
        </div>
        <div className="max-w-5xl mx-auto px-5 flex gap-6">
          {[{ k: 'comprar', label: 'Comprar', icon: ShoppingCart }, { k: 'historico', label: 'Histórico', icon: History }].map(t => (
            <button key={t.k} onClick={() => setView(t.k)} className={`pb-3 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${view === t.k ? 'border-indigo-500 text-white' : 'border-transparent text-white/40 hover:text-white/70'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      {view === 'comprar' && (
        <main className="relative max-w-5xl mx-auto px-5 py-8">
          <div className="mb-6 csUp">
            <h2 className="text-2xl font-bold text-white tracking-tight">Comprar créditos</h2>
            <p className="text-sm text-white/50 mt-1">Selecione uma integração para recarregar.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {me.integracoes.map((ig, idx) => (
              <button key={ig.id} onClick={() => { fecharCompra(); setSelInteg(ig); }} style={{ animationDelay: `${idx * 70}ms` }}
                className="csUp group text-left cs-glass rounded-2xl p-5 transition-all hover:-translate-y-1 hover:border-indigo-500/40">
                <div className="flex items-center gap-3.5">
                  <IntegIcon nome={ig.nome} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white truncate">{ig.nome}</p>
                    <p className="text-xs text-white/40 mt-0.5">{ig.tiers.length > 0 ? `desde ${brl(Math.min(...ig.tiers.map(t => t.preco_unitario)))} / crédito` : 'sem preços'}</p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-white/[0.05] group-hover:bg-indigo-500/20 flex items-center justify-center transition-colors"><ArrowRight className="w-4 h-4 text-white/40 group-hover:text-indigo-300" /></div>
                </div>
                {ig.meu_usuario_no_painel && (
                  <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between">
                    <span className="text-[11px] text-white/40">Sua conta: <span className="text-white/60 font-mono">{ig.meu_usuario_no_painel}</span></span>
                    {me.pode_autovincular && (
                      <span className="flex items-center gap-2">
                        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setVincModal({ ...ig, editando: true }); setVincUser(ig.meu_usuario_no_painel || ''); }} className="text-[11px] text-indigo-300 hover:text-indigo-200">editar</span>
                        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); desvincular(ig); }} className="text-[11px] text-red-400 hover:text-red-300">desvincular</span>
                      </span>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
          {me.integracoes.length === 0 && (me.disponiveis_para_vincular || []).length === 0 && <div className="cs-glass rounded-2xl p-10 text-center csUp"><Package className="w-10 h-10 text-white/20 mx-auto mb-3" /><p className="text-white/50 text-sm">Nenhuma integração disponível ainda.</p></div>}

          {/* Integrações disponíveis para AUTO-VINCULAR */}
          {(me.disponiveis_para_vincular || []).length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-white/70 mb-3">Vincular nova integração</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {me.disponiveis_para_vincular.map(ig => (
                  <button key={ig.id} onClick={() => { setVincModal(ig); setVincUser(''); }}
                    className="text-left cs-glass rounded-2xl p-5 transition-all hover:-translate-y-1 hover:border-indigo-500/40 border-dashed">
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center shrink-0 text-white/50">+</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white truncate">{ig.nome}</p>
                        <p className="text-xs text-white/40 mt-0.5">clique para vincular seu usuário</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </main>
      )}

      {/* MODAL DE VÍNCULO */}
      {vincModal && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] csFade" onClick={() => setVincModal(null)} />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setVincModal(null)}>
            <div className="w-full max-w-sm cs-glass rounded-2xl p-6 csPop" onClick={e => e.stopPropagation()}>
              <h3 className="text-white font-bold mb-1">{vincModal.editando ? 'Editar' : 'Vincular'} {vincModal.nome}</h3>
              <p className="text-xs text-white/50 mb-3">Digite seu usuário nesse painel. Vamos verificar se existe e vincular à sua conta.</p>
              {vincModal.provider === 'geradorpro' && (
                <div className="mb-4 rounded-xl bg-amber-500/15 border border-amber-500/40 p-3">
                  <p className="text-[13px] text-amber-300 font-semibold mb-1">⚠️ ATENÇÃO — Gerador Pro</p>
                  <p className="text-[12px] text-amber-200/90 leading-relaxed">No Gerador Pro, só é possível receber créditos se a sua conta for <b>MASTER</b> ou <b>ULTRA MASTER</b>. Se você <b>não for</b>, mesmo pagando, os créditos <b>NÃO serão depositados</b>. Confirme seu tipo de conta antes de comprar.</p>
                </div>
              )}
              <input value={vincUser} onChange={e => setVincUser(e.target.value)} placeholder="seu usuário no painel" autoFocus
                onKeyDown={e => e.key === 'Enter' && vincular()}
                className="cs-field w-full px-4 py-3 rounded-xl text-white text-sm mb-4" />
              <div className="flex gap-2">
                <button onClick={() => setVincModal(null)} className="flex-1 py-2.5 rounded-xl bg-white/[0.06] text-white text-sm">Cancelar</button>
                <button onClick={vincular} disabled={vincBusy} className="cs-btn flex-1 py-2.5 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                  {vincBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verificar e vincular
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {view === 'historico' && (
        <main className="relative max-w-5xl mx-auto px-5 py-8 csUp">
          <h2 className="text-2xl font-bold text-white tracking-tight mb-6">Meu histórico</h2>
          {!hist ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div> : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { icon: Package, label: 'Créditos', val: hist.total_creditos, grad: 'from-indigo-500/20 to-indigo-600/5' },
                  { icon: Wallet, label: 'Total gasto', val: brl(hist.total_gasto), grad: 'from-sky-500/20 to-sky-600/5' },
                  { icon: TrendingUp, label: 'Compras', val: hist.total_compras, grad: 'from-violet-500/20 to-violet-600/5' },
                ].map((c, i) => (
                  <div key={i} className={`cs-glass rounded-2xl p-5 bg-gradient-to-br ${c.grad}`}>
                    <c.icon className="w-5 h-5 text-white/60 mb-3" />
                    <p className="text-2xl font-bold text-white tracking-tight">{c.val}</p>
                    <p className="text-[11px] text-white/40 uppercase tracking-wider mt-0.5">{c.label}</p>
                  </div>
                ))}
              </div>
              {hist.por_integracao.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-white/70 mb-3">Por integração</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {hist.por_integracao.map((pi, i) => (
                      <div key={i} className="cs-glass rounded-xl p-4 flex items-center gap-3">
                        <IntegIcon nome={pi.nome} />
                        <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-white truncate">{pi.nome}</p><p className="text-[11px] text-white/40">{pi.compras} compra(s)</p></div>
                        <div className="text-right"><p className="text-lg font-bold text-white">{pi.creditos}</p><p className="text-[11px] text-white/50">{brl(pi.gasto)}</p></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <h3 className="text-sm font-semibold text-white/70 mb-3">Recargas recentes</h3>
              <div className="space-y-2">
                {hist.historico.length === 0 && <div className="cs-glass rounded-xl p-8 text-center"><p className="text-white/40 text-sm">Nenhuma compra ainda.</p></div>}
                {hist.historico.map((h) => (
                  <div key={h.id} className="cs-glass rounded-xl px-4 py-3.5 flex items-center justify-between">
                    <div className="min-w-0"><p className="text-sm text-white truncate"><span className="font-semibold">{h.quantidade}</span> créditos · <span className="text-white/50">{h.integracao}</span></p><p className="text-[11px] text-white/40 mt-0.5">{new Date(h.data).toLocaleString('pt-BR')}</p></div>
                    <div className="text-right shrink-0"><p className="text-sm font-bold text-white">{brl(h.valor)}</p>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${h.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>{h.status === 'approved' ? (h.depositado ? 'depositado' : 'pago') : 'pendente'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      )}

      {/* PAINEL LATERAL */}
      {selInteg && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 csFade" onClick={fecharPainel} />
          <aside className="fixed top-0 right-0 bottom-0 w-full max-w-md z-50 flex flex-col csSlide cs-glass border-l border-white/10">
            <div className="px-6 py-5 border-b border-white/[0.08] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <IntegIcon nome={selInteg.nome} />
                <div><p className="text-sm font-bold text-white">{selInteg.nome}</p><p className="text-[11px] text-white/40">{selInteg.meu_usuario_no_painel ? <>para <span className="text-indigo-300 font-mono">{selInteg.meu_usuario_no_painel}</span></> : 'Comprar créditos'}</p></div>
              </div>
              <button onClick={fecharPainel} className="w-8 h-8 rounded-lg hover:bg-white/[0.06] flex items-center justify-center text-white/40 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            </div>

            {pix ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center csPop">
                {pix.aprovado ? (
                  <>
                    <div className="w-20 h-20 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5"><Check className="w-10 h-10 text-emerald-400" /></div>
                    <p className="text-xl font-bold text-white">Pagamento aprovado</p>
                    <p className="text-sm text-white/50 mt-2">{pix.quantidade} créditos depositados no painel automaticamente.</p>
                    <button onClick={fecharCompra} className="cs-btn mt-7 px-6 py-2.5 rounded-xl text-white text-sm font-semibold">Nova compra</button>
                  </>
                ) : (
                  <>
                    <p className="text-[11px] text-white/40 uppercase tracking-widest mb-1">Total</p>
                    <p className="text-4xl font-bold text-white tracking-tight">{brl(pix.total)}</p>
                    <p className="text-white/40 text-sm mb-6">{pix.quantidade} créditos</p>
                    {pix.qr_code && <div className="p-3 bg-white rounded-2xl shadow-2xl"><img src={pix.qr_code.startsWith('data:') ? pix.qr_code : `data:image/png;base64,${pix.qr_code}`} alt="PIX" className="w-52 h-52" /></div>}
                    {pix.pix_code && (
                      <div className="mt-5 w-full flex items-center gap-2 bg-white/[0.04] border border-white/[0.1] rounded-xl p-3">
                        <span className="text-xs text-white/50 font-mono truncate flex-1 text-left">{pix.pix_code}</span>
                        <button onClick={() => { navigator.clipboard.writeText(pix.pix_code); toast.success('Copiado'); }} className="text-indigo-300 shrink-0 px-1 hover:text-indigo-200"><Copy className="w-4 h-4" /></button>
                      </div>
                    )}
                    <p className="text-white/40 text-xs mt-5 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Verificando pagamento</p>
                    <p className="text-white/25 text-[11px] mt-1.5">Pode fechar. O depósito é automático ao aprovar.</p>
                  </>
                )}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-6">
                {(selInteg.modo === 'pacotes' ? (selInteg.pacotes || []).length > 0 : selInteg.tiers.length > 0) ? (
                  selInteg.modo === 'pacotes' ? (
                    <>
                      <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">Escolha um pacote</p>
                      <div className="space-y-2.5">
                        {(selInteg.pacotes || []).map((p, i) => {
                          const sel = parseInt(qtd) === p.creditos;
                          return (
                            <button key={i} onClick={() => setQtd(String(p.creditos))}
                              className={`w-full text-left rounded-xl border p-4 transition-all ${sel ? 'border-indigo-400 bg-indigo-500/10 ring-1 ring-indigo-400/40' : 'border-white/[0.08] bg-white/[0.03] hover:border-white/20'}`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-white font-bold text-lg">{p.creditos} <span className="text-sm font-normal text-white/50">créditos</span></p>
                                  {p.nome && <p className="text-[11px] text-white/40">{p.nome}</p>}
                                </div>
                                <div className="text-right">
                                  <p className="text-white font-bold text-xl">{brl(p.preco)}</p>
                                  <p className="text-[10px] text-white/40">{brl(p.preco / p.creditos)}/crédito</p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {me?.checkout_unificado ? (
                        <button onClick={() => comprar('cartao')} disabled={!cotacao || comprando} className="cs-btn w-full mt-5 py-3.5 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40">
                          {comprando ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} {cotacao ? `Pagar ${brl(cotacao.total)} (PIX ou Cartão)` : 'Selecione um pacote'}
                        </button>
                      ) : (
                        <>
                          <button onClick={() => comprar('pix')} disabled={!cotacao || comprando} className="cs-btn w-full mt-5 py-3.5 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40">
                            {comprando ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} {cotacao ? `${me?.suporta_cartao ? 'PIX' : 'Pagar'} ${brl(cotacao.total)}` : 'Selecione um pacote'}
                          </button>
                          {me?.suporta_cartao && cotacao && (
                            <button onClick={() => comprar('cartao')} disabled={comprando} className="w-full mt-2 py-3 rounded-xl border border-white/[0.15] text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-white/[0.04] disabled:opacity-40">
                              💳 Pagar com cartão
                            </button>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">Tabela de preços</p>
                      <div className="space-y-2 mb-6">
                        {selInteg.tiers.map((t, i) => (
                          <div key={i} className="flex justify-between items-center text-sm bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2.5">
                            <span className="text-white/50">{(!t.max_qtd || t.max_qtd <= 0) ? `a partir de ${t.min_qtd}` : `${t.min_qtd}–${t.max_qtd}`} créditos</span>
                            <span className="text-white font-semibold">{brl(t.preco_unitario)}<span className="text-white/30 text-xs font-normal">/un</span></span>
                          </div>
                        ))}
                        {selInteg.max_por_compra && <p className="text-[11px] text-white/30 pt-1">Máximo {selInteg.max_por_compra} por compra</p>}
                      </div>
                      <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2 block">Quantidade</label>
                      <input type="number" value={qtd} onChange={e => setQtd(e.target.value)} placeholder="0" autoFocus className="cs-field w-full px-4 py-3.5 rounded-xl text-white text-2xl font-bold" />
                      {cotacao && (
                        <div className="mt-4 flex items-center justify-between bg-gradient-to-r from-indigo-500/15 to-transparent border border-indigo-500/20 rounded-xl px-4 py-3.5 csPop">
                          <span className="text-white/60 text-sm flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-indigo-400" /> {cotacao.quantidade} × {brl(cotacao.preco_unitario)}</span>
                          <span className="text-white font-bold text-xl">{brl(cotacao.total)}</span>
                        </div>
                      )}
                      <button onClick={() => comprar(me?.checkout_unificado ? 'cartao' : 'pix')} disabled={!cotacao || comprando} className="cs-btn w-full mt-5 py-3.5 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40">
                        {comprando ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} {me?.checkout_unificado ? 'Pagar (PIX ou Cartão)' : (me?.suporta_cartao ? 'Pagar com PIX' : 'Gerar PIX')}
                      </button>
                      {me?.suporta_cartao && !me?.checkout_unificado && cotacao && (
                        <button onClick={() => comprar('cartao')} disabled={comprando} className="w-full mt-2 py-3 rounded-xl border border-white/[0.15] text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-white/[0.04] disabled:opacity-40">
                          💳 Pagar com cartão
                        </button>
                      )}
                    </>
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center"><Package className="w-10 h-10 text-white/20 mb-3" /><p className="text-white/40 text-sm">Sem preços configurados para esta integração.</p></div>
                )}
              </div>
            )}
          </aside>
        </>
      )}
    </div>
  );
}
